import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Clear, ExpandLess, ExpandMore, HighlightOffRounded, Publish } from '@material-ui/icons';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DayjsUtils from '@date-io/dayjs';
import dayjs from 'dayjs';
import { ApiClient } from '../../api-client/api-client';
import {
  ApprovedTaskInput,
  DocumentPriorityType,
  DocumentTemplateType,
  DraftTask,
  IFile,
  IProjectUser,
  JobStatusType,
} from '../../api-client/autogenerated';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCurrentProjectSubscriberId,
  getProjectId,
  getProjectUsersState,
} from '../../features/project/selectors';
import { getSubscriberSecurityGroupState } from '../../features/security/selectors';
import AssignedUserText from '../custom-components/AssignedUserText';
import { CancelButton } from '../custom-components/CustomButtons';
import StatusPill from '../custom-components/StatusPill';
import {
  DocumentFileTree,
  generateUniqueId,
  getDocumentTreesForAssociations,
  getUserFriendlyDocumentTemplateNamePlural,
  sortLastNamesDesc,
  waitForFileToBeVerifiedByUrl,
} from '../../scripts/utils';
import { uploadToStaging } from '../../models/api/filesystem';
import { getMeetingMinutesTaskSummary } from '../../models/api/meeting-minutes-tasks';
import { MULTI_PART_FILE_SIZE } from '../../scripts/constants';
import { addSnackbar } from '../../features/snackbar/actions';
import FileUploadDialog from '../dialogs/FileUploadDialog';
import AssociateDocumentDialog from '../add-associations/AssociateDocumentDialog';
import { useAppUtilsContext } from '../AppUtilsContext';
import PDFIcon from '../icons/PDF-icon';
import DividerWithText from '../custom-components/DividerWithText';
import { getDocumentState } from '../../features/document/selectors';
import ManagePermissionsDialog, {
  ManagePermissionsDialogType,
} from '../design/ManagePermissionsDialog';
import InlineNotificationList from './user-groups/InlineNotificationList';

interface Props {
  open: boolean;
  handleClose: () => void;
  fileId: string;
  /** The source document's type, used only for the dialog heading. */
  sourceDocumentType?: DocumentTemplateType;
}

interface ProjectUserOption {
  id: string;
  name: string;
  email: string;
  /** user.company?.name, falling back to the denormalized user.companyName. */
  companyName: string | null;
  /** projectUser.securityGroup.name, or "Subscriber Admin" for subscriber admins. */
  securityGroupName: string | null;
}

/** One reviewable row: the model draft plus the architect's edits/decision. */
interface ReviewRow {
  /**
   * Unique client-side key for UI identity (React key, selection, edits). Generated
   * per row so selection/editing never collides even if the backend draft-task ids
   * are missing or duplicated.
   */
  rowKey: string;
  /** Backend draft-task id, sent when publishing/discarding this specific task. */
  id: string;
  /** Whether this row is checked for a bulk publish/discard action. Default false. */
  selected: boolean;
  /** When true, the task is informational: no assignee, no priority. */
  forInformationOnly: boolean;
  title: string;
  description: string;
  dueDate: string | null;
  assigneeUserId: string | null;
  /** Raw name from the transcript, kept so unmatched owners can be surfaced. */
  rawAssigneeName: string | null;
  confidence: number;
  /** Always defaults to Medium; the extraction model never determines this value. */
  priority: DocumentPriorityType;
  /** Files the architect uploaded (staged to S3) to attach to this task on publish. */
  uploadedFiles: { name: string; stagingKey: string }[];
  /** Existing documents the architect tagged to associate with this task on publish. */
  associatedDocs: { id: string; title: string; type: string }[];
  /**
   * The assignee the extraction originally matched, kept alongside the editable assigneeUserId
   * purely to decide which company group this row sits in. Reassigning a row updates
   * assigneeUserId but not this, so the row stays in its group instead of jumping to another
   * one while the architect works through the list fixing assignees. Stored as an id rather
   * than a resolved company name so the name still resolves correctly if the project's users
   * land in Redux after the draft does. null when the extraction matched nobody ("Unmatched").
   */
  groupAssigneeUserId: string | null;
}

/** A row is publishable once it has a title and either an assignee or is informational. */
const isRowPublishable = (row: ReviewRow): boolean =>
  row.title.trim().length > 0 && !!row.assigneeUserId;

/** The "Customize each task" list, bucketed by the assignee's company for bulk review. */
interface CompanyGroup {
  /** Stable React/collapse-state key: the company name, or UNASSIGNED_GROUP_KEY. */
  key: string;
  companyName: string;
  rows: ReviewRow[];
  /** Deduplicated, alphabetized names of every assignee with a task in this group. */
  userNames: string[];
}

const UNASSIGNED_GROUP_KEY = '__unassigned__';

const PRIORITY_LABELS: Record<DocumentPriorityType, string> = {
  [DocumentPriorityType.High]: 'High',
  [DocumentPriorityType.Medium]: 'Medium',
  [DocumentPriorityType.Low]: 'Low',
  [DocumentPriorityType.ForInformationOnly]: 'For Information Only',
};

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 40;

/** Default due date for every draft task: today plus 7 days. Adjustable per-row or in bulk. */
const getDefaultDueDate = (): string => dayjs().add(7, 'days').toISOString();

const toReviewRow = (
  task: DraftTask,
  defaultDueDate: string,
  defaultPriority: DocumentPriorityType,
): ReviewRow => ({
  groupAssigneeUserId: task.assigneeUserId ?? null,
  rowKey: generateUniqueId(),
  id: task.id,
  selected: false,
  forInformationOnly: false,
  title: task.title,
  description: task.description,
  dueDate: defaultDueDate,
  assigneeUserId: task.assigneeUserId ?? null,
  rawAssigneeName: task.assigneeName ?? null,
  confidence: task.confidence,
  priority: defaultPriority,
  uploadedFiles: [],
  associatedDocs: [],
});

/** projectUser.securityGroup.name, or "Subscriber Admin" for the project's subscriber admins. */
const getSecurityGroupDisplayName = (
  pu: IProjectUser,
  subscriberId: string | undefined,
): string => {
  if (pu.user?.adminOfSubscriberId && pu.user.adminOfSubscriberId === subscriberId) {
    return 'Subscriber Admin';
  }
  if (pu.user?.isSiteAdmin) {
    return 'Site Admin';
  }
  return pu.securityGroup?.name ?? 'No security group';
};

/** Sorts by company name ascending (nulls last), then last name descending. */
function sortByCompanyThenLastNameDesc<T>(
  items: T[],
  getCompanyName: (item: T) => string | null,
  getName: (item: T) => string,
): T[] {
  return [...items].sort((a, b) => {
    const companyA = getCompanyName(a);
    const companyB = getCompanyName(b);
    if (companyA == null && companyB == null) return 0;
    if (companyA == null) return 1;
    if (companyB == null) return -1;
    const companyCompare = companyA.localeCompare(companyB);
    if (companyCompare !== 0) return companyCompare;
    // sortLastNamesDesc sorts ascending when called directly (see its other call
    // sites, e.g. UsersTable.tsx); negate it here for descending order.
    return sortLastNamesDesc(getName(a), getName(b));
  });
}

const useStyles = makeStyles({
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 16px 8px 16px',
  },
  // MUI v4 only paints a Checkbox with the primary color once it is checked (colorPrimary
  // scopes the color under &$checked), so the indeterminate dash kept the default grey and a
  // partially selected company group read as unselected. Paired class names so the rule wins on
  // specificity over .MuiCheckbox-root regardless of stylesheet injection order. #0947B9 is the
  // primary already hardcoded throughout this file, including this same group header.
  groupCheckbox: {
    '&$groupCheckboxIndeterminate': {
      color: '#0947B9',
    },
  },
  groupCheckboxIndeterminate: {},
  title: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '26px',
    lineHeight: '30px',
    textAlign: 'left',
    color: '#FFFFFF',
    textTransform: 'none',
    padding: '16px 48px 16px 16px',
    background: 'linear-gradient(225deg, #00308C 0%, #002366 100%)',
  },
  card: {
    position: 'relative',
    padding: 16,
    // The outlined-input notch gap is sized from the label at 0.75em, independent of
    // the enlarged fieldLabel. Scale the notch legend up so the wider floating label
    // fits the gap — but only while unfocused, since a focused field's label returns to
    // the default size (and so the notch returns to its default gap).
    '& .MuiOutlinedInput-root:not(.Mui-focused) .MuiOutlinedInput-notchedOutline legend': {
      fontSize: '1em',
    },
  },
  // Off-white surface matching the document details page (#F9F9F9).
  offWhitePaper: {
    backgroundColor: '#F9F9F9',
  },
  // Larger field labels for the draft task cards. When the field is not focused the
  // label uses a darker gray; the focused (primary) color is left to MUI.
  fieldLabel: {
    '&:not(.Mui-focused)': {
      color: '#464546',
      fontSize: '1.25rem',
    },
  },
  rootIconButton: {
    padding: 0,
    color: '#FFFFFF',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  deleteButton: {
    minHeight: '32px',
    padding: '6px 30px',
    backgroundColor: '#ED3F26',
    lineHeight: 1,
    color: 'white',
    '&:hover': {
      backgroundColor: '#FF5D45',
    },
  },
  // Same file-box treatment as ReviewAndReturn's "Current Supporting File" box.
  fileBox: {
    display: 'flex',
    flexDirection: 'row',
    width: '90%',
    padding: '12px',
    border: '1px solid #BFBFBF',
    boxSizing: 'border-box',
    borderRadius: '4px',
    minWidth: 0,
    overflow: 'hidden',
  },
  fileName: {
    paddingLeft: '8px',
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '11px',
    lineHeight: '29px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    color: '#464546',
  },
});

/**
 * Review panel for the Design Meeting Minutes → Task flow. Runs the async
 * extraction, lets the architect edit/confirm the draft, and creates the Task
 * documents only on explicit confirmation. Nothing is created or assigned until
 * the architect clicks "Create tasks".
 */
const MeetingMinutesTaskReviewPanel: React.FC<Props> = ({
  open,
  handleClose,
  fileId,
  sourceDocumentType,
}) => {
  const classes = useStyles();
  // Meeting Minutes produce "CA Task" documents; Design Meeting Minutes produce "Task".
  const targetTaskType =
    sourceDocumentType === DocumentTemplateType.MeetingMinutes
      ? DocumentTemplateType.CaTask
      : DocumentTemplateType.Task;
  const heading =
    targetTaskType === DocumentTemplateType.CaTask
      ? 'Meeting Minutes — CA Task Extraction'
      : 'Design Meeting Minutes — Task Extraction';
  const dispatch = useDispatch();
  const document = useSelector(getDocumentState)!;
  const projectId = useSelector(getProjectId)!;
  const projectUsers = useSelector(getProjectUsersState);
  const subscriberId = useSelector(getCurrentProjectSubscriberId);
  const securityGroups = useSelector(getSubscriberSecurityGroupState);
  const {
    documentTemplates: { getTemplateId },
  } = useAppUtilsContext();
  const isMobile = useMediaQuery('(max-width:500px)');

  const documentId = document.id;

  // Associating each created task with this source document (see createTasksFromDraft)
  // automatically copies these current followers onto every task's notification list —
  // this is the set "Define Notification List" previews and lets the architect opt out of.
  const sourceDocumentUserIds = useMemo(
    () => Array.from(new Set((document.documentUserList ?? []).map((du) => du.userId))),
    [document.documentUserList],
  );
  const sourceDocumentUserGroupIds = useMemo(
    () => Array.from(new Set((document.documentUserGroupList ?? []).map((dg) => dg.userGroupId))),
    [document.documentUserGroupList],
  );

  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [users, setUsers] = useState<ProjectUserOption[]>([]);
  const [bulkDueDate, setBulkDueDate] = useState<string | null>(null);
  const [bulkPriority, setBulkPriority] = useState<DocumentPriorityType>(
    DocumentPriorityType.Medium,
  );
  // The full effective notification list for every task created by this publish: seeded from
  // sourceDocumentUserIds/sourceDocumentUserGroupIds (what the association-copy side effect
  // would add) and editable — checking a box back off removes it from that automatic copy,
  // and adding someone new here follows AddDocumentDialog's "Define Notification List".
  const [associatedUsers, setAssociatedUsers] = useState<string[]>([]);
  const [associatedGroups, setAssociatedGroups] = useState<string[]>([]);
  const [associationDialogOpen, setAssociationDialogOpen] = useState(false);
  // The meeting minutes file that gets copied onto every published task, shown read-only
  // as "Default File Attachment" (see createTasksFromDraft's copyFileIds on the backend).
  const [sourceFile, setSourceFile] = useState<IFile | null>(null);
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'extracting' | 'ready' | 'publishing' | 'error'
  >('idle');
  const [message, setMessage] = useState<string | null>(null);
  // Separates "the transcript yielded nothing" from "everything extracted has since been
  // published or discarded" in the empty state. Set when the architect clears the last rows in
  // this session, and seeded from the extraction summary when a reopened draft comes back empty
  // (getLatestDraft only returns rows still in draft status, so a fully-resolved document is
  // otherwise indistinguishable from one that never produced any tasks).
  const [hasResolvedTasks, setHasResolvedTasks] = useState(false);
  // Rows awaiting discard confirmation; non-null opens the confirmation dialog.
  const [discardCandidates, setDiscardCandidates] = useState<ReviewRow[] | null>(null);
  const [isDiscarding, setIsDiscarding] = useState(false);
  // Row currently being published on its own; its card shows a loading indicator in
  // place of the per-item Discard/Publish buttons until it publishes and drops off.
  const [publishingRowKey, setPublishingRowKey] = useState<string | null>(null);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // "Tag Existing Documents" tree for the project, loaded once and cloned per row so a
  // row's selections never leak into another. The source doc is excluded (it is always
  // auto-associated on publish).
  const baseDocTree = useRef<DocumentFileTree[]>([]);
  // Per-row file upload dialog (which row is uploading; files staged before publish).
  const [fileUploadRowKey, setFileUploadRowKey] = useState<string | null>(null);
  const [localFiles, setLocalFiles] = useState<File[]>([]);
  const [fileUploadMessage, setFileUploadMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  // Per-row "Tag Existing Documents" dialog (which row is tagging; its working tree).
  const [associateRowKey, setAssociateRowKey] = useState<string | null>(null);
  const [workingDocTree, setWorkingDocTree] = useState<DocumentFileTree[]>([]);

  const loadUsers = useCallback(() => {
    // Only users whose security group can review/complete tasks
    // (taskAssignmentPermission) AND has at least "Personal" (non-null taskManagerPermission)
    // on the target task type are assignable. Site/subscriber admins have full access and are
    // always eligible.
    const targetTemplateId = getTemplateId(targetTaskType);
    const eligible = projectUsers.filter((pu) => {
      if (pu.user?.isSiteAdmin) return true;
      if (pu.user?.adminOfSubscriberId === subscriberId) return true;
      const group = securityGroups.find((g) => g.id === pu.securityGroupId);
      if (!group?.taskAssignmentPermission) return false;
      const taskManagerPermission = group.securityGroupDocumentTemplateList?.find(
        (dt) => dt.documentTemplateId === targetTemplateId,
      )?.taskManagerPermission;
      return taskManagerPermission != null;
    });

    const options: ProjectUserOption[] = eligible.map((pu) => ({
      id: pu.userId,
      name: pu.user?.name ?? '',
      email: pu.user?.email ?? '',
      companyName: pu.user?.company?.name ?? pu.user?.companyName ?? null,
      securityGroupName: getSecurityGroupDisplayName(pu, subscriberId),
    }));
    setUsers(
      sortByCompanyThenLastNameDesc(
        options,
        (u) => u.companyName,
        (u) => u.name,
      ),
    );
  }, [projectUsers, subscriberId, securityGroups, getTemplateId, targetTaskType]);

  // userId -> company name + full name, used to sort tasks by their assignee.
  const assigneeInfoByUserId = useMemo(() => {
    const map = new Map<string, { companyName: string | null; name: string }>();
    projectUsers.forEach((pu) =>
      map.set(pu.userId, {
        companyName: pu.user?.company?.name ?? pu.user?.companyName ?? null,
        name: pu.user?.name ?? '',
      }),
    );
    return map;
  }, [projectUsers]);

  const applyDraft = useCallback(
    (draftTasks: DraftTask[]) => {
      const sorted = sortByCompanyThenLastNameDesc(
        draftTasks,
        (t) =>
          t.assigneeUserId ? assigneeInfoByUserId.get(t.assigneeUserId)?.companyName ?? null : null,
        (t) => (t.assigneeUserId ? assigneeInfoByUserId.get(t.assigneeUserId)?.name ?? '' : ''),
      );
      const defaultDueDate = getDefaultDueDate();
      const defaultPriority = DocumentPriorityType.Medium;
      setBulkDueDate(defaultDueDate);
      setBulkPriority(defaultPriority);
      // Seed the previewed notification list with exactly what the association-copy side
      // effect would add, so unchecking someone here is the only way to actually keep them
      // off — see excludeUserIds/excludeGroupIds in publishRows.
      setAssociatedUsers(sourceDocumentUserIds);
      setAssociatedGroups(sourceDocumentUserGroupIds);
      setRows(sorted.map((t) => toReviewRow(t, defaultDueDate, defaultPriority)));
      // An empty draft is ambiguous on its own — ask the summary whether anything was ever
      // published or discarded so the empty state can say which happened.
      if (sorted.length === 0) {
        void getMeetingMinutesTaskSummary(projectId, documentId)
          .then((summary) =>
            setHasResolvedTasks(summary.publishedCount + summary.discardedCount > 0),
          )
          .catch(() => undefined);
      }
      setStatus('ready');
    },
    [
      assigneeInfoByUserId,
      sourceDocumentUserIds,
      sourceDocumentUserGroupIds,
      projectId,
      documentId,
    ],
  );

  const poll = useCallback(
    async (attempt: number) => {
      const res = await ApiClient.getDraft({ projectId, documentId });
      const result = res.data;
      if (result.status === JobStatusType.Complete && result.draft) {
        applyDraft(result.draft.tasks);
        return;
      }
      if (result.status === JobStatusType.Canceled) {
        setStatus('error');
        setMessage(result.error || 'Task extraction failed.');
        return;
      }
      if (attempt >= MAX_POLLS) {
        setStatus('error');
        setMessage('Timed out waiting for task extraction.');
        return;
      }
      pollTimer.current = setTimeout(() => void poll(attempt + 1), POLL_INTERVAL_MS);
    },
    [projectId, documentId, applyDraft],
  );

  const startExtraction = useCallback(async () => {
    setStatus('extracting');
    setMessage(null);
    try {
      await ApiClient.extractTasks({ projectId, documentId });
      await poll(0);
    } catch (e) {
      setStatus('error');
      setMessage('Could not start task extraction.');
    }
  }, [projectId, documentId, poll]);

  // Each time the dialog opens: load users, and either show an existing draft, resume
  // polling a job already in progress, or start extraction automatically so the architect
  // never has to click "Extract tasks" themselves. Skipped if a draft is already loaded
  // (e.g. reopening after a previous run) so in-progress edits aren't overwritten.
  useEffect(() => {
    if (!open) return;
    loadUsers();
    if (
      rows.length > 0 ||
      status === 'loading' ||
      status === 'extracting' ||
      status === 'publishing'
    )
      return;
    setStatus('loading');
    void (async () => {
      try {
        const res = await ApiClient.getDraft({ projectId, documentId });
        const result = res.data;
        if (result.status === JobStatusType.Complete && result.draft) {
          applyDraft(result.draft.tasks);
        } else if (
          result.status === JobStatusType.Pending ||
          result.status === JobStatusType.Initiated
        ) {
          setStatus('extracting');
          await poll(0);
        } else {
          // No usable draft yet (never run, or a prior run failed/was canceled).
          await startExtraction();
        }
      } catch {
        await startExtraction();
      }
    })();
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId, documentId]);

  // Load the taggable-documents tree once per open, excluding the source document.
  useEffect(() => {
    if (!open) return;
    void getDocumentTreesForAssociations(projectId, [documentId], undefined).then((tree) => {
      baseDocTree.current = tree;
    });
  }, [open, projectId, documentId]);

  // Load the source document's most recent PDF once per open, for the read-only "Default
  // File Attachment" preview (skipInsertViewNotification: this fetch shouldn't count as the
  // architect viewing the document).
  useEffect(() => {
    if (!open) return;
    const mostRecentFile = document.files?.find((f) => f.id === fileId);
    if (mostRecentFile) setSourceFile(mostRecentFile);
  }, [open, document]);

  // --- Per-row "Tag Existing Documents" ---

  const openAssociateDialog = (row: ReviewRow) => {
    const tagged = new Set(row.associatedDocs.map((d) => d.id));
    // Clone the base tree and pre-check whatever this row already tagged.
    const tree: DocumentFileTree[] = baseDocTree.current.map((t) => ({
      type: t.type,
      documents: t.documents.map((d) => ({ ...d, checked: tagged.has(d.id) })),
    }));
    setWorkingDocTree(tree);
    setAssociateRowKey(row.rowKey);
  };

  const closeAssociateDialog = () => {
    if (associateRowKey) {
      // Keep each tagged doc's type so the summary can be grouped by document type.
      const tagged = workingDocTree.flatMap((t) =>
        t.documents
          .filter((d) => d.checked)
          .map((d) => ({ id: d.id, title: d.title, type: t.type })),
      );
      updateRow(associateRowKey, { associatedDocs: tagged });
    }
    setAssociateRowKey(null);
    setWorkingDocTree([]);
  };

  // --- Per-row file upload (staged to S3, attached to the task on publish) ---

  const uploadRowFilesToStaging = async () => {
    const rowKey = fileUploadRowKey;
    if (rowKey && localFiles.length > 0) {
      const staged: { name: string; stagingKey: string }[] = [];
      for (const file of localFiles) {
        setFileUploadMessage(`Uploading ${file.name}...`);
        const response = await uploadToStaging(
          { fileName: file.name, useMultiPartUpload: file.size > MULTI_PART_FILE_SIZE },
          file,
          (event: any) => setUploadProgress(Math.round((100 * event.loaded) / event.total)),
        );
        setFileUploadMessage('Verifying upload...');
        setUploadProgress(0);
        const verified = await waitForFileToBeVerifiedByUrl(response.s3Key);
        if (verified) {
          staged.push({ name: file.name, stagingKey: response.s3Key });
        } else {
          dispatch(
            addSnackbar({
              id: Date.now(),
              message: `Something went wrong while uploading ${file.name}. Please try again.`,
              severity: 'error',
            }),
          );
          break;
        }
      }
      setRows((prev) =>
        prev.map((row) =>
          row.rowKey === rowKey
            ? { ...row, uploadedFiles: [...row.uploadedFiles, ...staged] }
            : row,
        ),
      );
      setLocalFiles([]);
    }
    setFileUploadRowKey(null);
  };

  const removeUploadedFile = (rowKey: string, stagingKey: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.rowKey === rowKey
          ? { ...row, uploadedFiles: row.uploadedFiles.filter((f) => f.stagingKey !== stagingKey) }
          : row,
      ),
    );
  };

  const removeTaggedDoc = (rowKey: string, docId: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.rowKey === rowKey
          ? { ...row, associatedDocs: row.associatedDocs.filter((d) => d.id !== docId) }
          : row,
      ),
    );
  };

  const updateRow = (rowKey: string, patch: Partial<ReviewRow>) => {
    setRows((prev) => prev.map((row) => (row.rowKey === rowKey ? { ...row, ...patch } : row)));
  };

  const applyBulkDueDate = (iso: string | null) => {
    setBulkDueDate(iso);
    setRows((prev) => prev.map((row) => ({ ...row, dueDate: iso })));
  };

  const applyBulkPriority = (priority: DocumentPriorityType) => {
    setBulkPriority(priority);
    setRows((prev) => prev.map((row) => ({ ...row, priority })));
  };

  const selectedRows = rows.filter((row) => row.selected);
  const canPublishSelected = selectedRows.length > 0 && selectedRows.every(isRowPublishable);

  // A row is "matched" when its assignee is one of the eligible project team members. Matched
  // rows are the ones ready to publish; unmatched rows still need an assignee (or a discard).
  const isRowMatched = (row: ReviewRow): boolean => users.some((u) => u.id === row.assigneeUserId);
  const matchedRows = rows.filter(isRowMatched);
  const unmatchedRows = rows.filter((row) => !isRowMatched(row));
  const publishableMatchedRows = matchedRows.filter(isRowPublishable);

  // "Customize each task" bucketed by the company of the row's ORIGINAL extracted assignee
  // (row.groupAssigneeUserId), so the architect can review, select, and bulk discard/publish one
  // company's tasks at a time. Reassigning a row deliberately leaves it where it is — grouping
  // by the current assignee made rows jump to another group mid-edit, reshuffling the list out
  // from under the architect while they worked through it. Grouped by company rather than by
  // "matched" status: a row can be a real project user with a company yet still show the
  // unmatched badge (assignee ineligible for this task type), so company is the more useful axis
  // for a company-by-company review pass.
  const companyGroups = useMemo<CompanyGroup[]>(() => {
    const groups = new Map<string, CompanyGroup>();
    rows.forEach((row) => {
      const companyName = row.groupAssigneeUserId
        ? assigneeInfoByUserId.get(row.groupAssigneeUserId)?.companyName ?? null
        : null;
      const key = companyName ?? UNASSIGNED_GROUP_KEY;
      const existing = groups.get(key);
      if (existing) existing.rows.push(row);
      else
        groups.set(key, {
          key,
          companyName: companyName ?? 'Unmatched',
          rows: [row],
          userNames: [],
        });
    });
    groups.forEach((group) => {
      const names = new Set<string>();
      group.rows.forEach((row) => {
        const name = row.assigneeUserId ? assigneeInfoByUserId.get(row.assigneeUserId)?.name : null;
        if (name) names.add(name);
      });
      group.userNames = Array.from(names).sort((a, b) => a.localeCompare(b));
    });
    return Array.from(groups.values()).sort((a, b) => {
      if (a.key === UNASSIGNED_GROUP_KEY) return 1;
      if (b.key === UNASSIGNED_GROUP_KEY) return -1;
      return a.companyName.localeCompare(b.companyName);
    });
  }, [rows, assigneeInfoByUserId]);

  // Which company groups are expanded; all groups start collapsed.
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<Set<string>>(new Set());
  const toggleGroupCollapsed = (key: string) => {
    setExpandedGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setGroupSelected = (group: CompanyGroup, selected: boolean) => {
    const keys = new Set(group.rows.map((row) => row.rowKey));
    setRows((prev) => prev.map((row) => (keys.has(row.rowKey) ? { ...row, selected } : row)));
  };

  /**
   * Publishes the given rows (creates Task documents) and removes them from the list.
   * When `individualRowKey` is given (the per-item Publish button), the loading state is
   * shown inside that item's card; otherwise (Publish Selected) it shows in the action bar.
   */
  const publishRows = useCallback(
    async (toPublish: ReviewRow[], individualRowKey?: string) => {
      const publishable = toPublish.filter(isRowPublishable);
      if (publishable.length === 0) return;

      const approved: ApprovedTaskInput[] = publishable.map((row) => ({
        draftTaskId: row.id,
        title: row.title.trim(),
        description: row.description,
        // For-information-only tasks keep whatever assignee the architect set and use the
        // FYI priority; toggling the box never changes the assignee.
        dueDate: row.dueDate,
        assigneeUserId: row.assigneeUserId,
        priority: row.forInformationOnly ? DocumentPriorityType.ForInformationOnly : row.priority,
        associatedDocumentIds: row.associatedDocs.map((d) => d.id),
        stagingFiles: row.uploadedFiles.map((f) => ({ s3Key: f.stagingKey })),
      }));

      // associatedUsers/associatedGroups is the full previewed notification list (seeded from
      // sourceDocumentUserIds/sourceDocumentUserGroupIds). Anyone the architect unchecked from
      // that preview must be explicitly excluded — otherwise the association-copy side effect
      // on the backend adds them back regardless of what's sent as associatedUserIds. Anyone
      // added beyond the preview is a genuine extra, sent the same way as before.
      const excludeUserIds = sourceDocumentUserIds.filter((id) => !associatedUsers.includes(id));
      const excludeGroupIds = sourceDocumentUserGroupIds.filter(
        (id) => !associatedGroups.includes(id),
      );
      const extraUserIds = associatedUsers.filter((id) => !sourceDocumentUserIds.includes(id));
      const extraGroupIds = associatedGroups.filter(
        (id) => !sourceDocumentUserGroupIds.includes(id),
      );

      if (individualRowKey) setPublishingRowKey(individualRowKey);
      else setStatus('publishing');
      setMessage(null);
      try {
        const res = await ApiClient.confirm({
          projectId,
          documentId,
          confirmTasksRequest: {
            tasks: approved,
            associatedUserIds: extraUserIds,
            associatedGroupIds: extraGroupIds,
            excludeUserIds,
            excludeGroupIds,
          },
        });
        const publishedKeys = new Set(publishable.map((row) => row.rowKey));
        setRows((prev) => prev.filter((row) => !publishedKeys.has(row.rowKey)));
        setHasResolvedTasks(true);
        const count = res.data.createdDocumentIds.length;
        dispatch(
          addSnackbar({
            id: Date.now(),
            message: `Published ${count} task${count === 1 ? '' : 's'}.`,
            severity: 'success',
          }),
        );
        if (!individualRowKey) setStatus('ready');
      } catch {
        setStatus('error');
        setMessage('Failed to publish tasks.');
      } finally {
        if (individualRowKey) setPublishingRowKey(null);
      }
    },
    [
      projectId,
      documentId,
      associatedUsers,
      associatedGroups,
      sourceDocumentUserIds,
      sourceDocumentUserGroupIds,
    ],
  );

  /** Confirms the pending discard: hides the tasks server-side and drops them from the list. */
  const confirmDiscard = useCallback(async () => {
    const candidates = discardCandidates;
    if (!candidates || candidates.length === 0) return;
    setIsDiscarding(true);
    setMessage(null);
    try {
      // Backend hides by draft-task id; local removal is by the unique rowKey so a
      // missing/duplicate backend id can't drop the wrong rows.
      const taskIds = candidates.map((row) => row.id).filter((id): id is string => !!id);
      if (taskIds.length > 0) {
        await ApiClient.discardDraftTasks({
          projectId,
          documentId,
          discardTasksRequest: { taskIds },
        });
      }
      const discardedKeys = new Set(candidates.map((row) => row.rowKey));
      setRows((prev) => prev.filter((row) => !discardedKeys.has(row.rowKey)));
      setHasResolvedTasks(true);
      dispatch(
        addSnackbar({
          id: Date.now(),
          message: `Discarded ${candidates.length} task${candidates.length === 1 ? '' : 's'}.`,
          severity: 'success',
        }),
      );
    } catch {
      setStatus('error');
      setMessage('Failed to discard tasks.');
    } finally {
      setIsDiscarding(false);
      setDiscardCandidates(null);
    }
  }, [discardCandidates, projectId, documentId]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ className: classes.offWhitePaper }}
      >
        <DialogTitle disableTypography className={classes.title}>
          {heading}
          <IconButton
            style={{ right: '20px', top: '20px', position: 'absolute' }}
            onClick={handleClose}
            classes={{
              root: classes.rootIconButton,
            }}
          >
            <HighlightOffRounded />
          </IconButton>
        </DialogTitle>
        <MuiPickersUtilsProvider utils={DayjsUtils}>
          <DialogContent>
            {message && (
              <Typography color={status === 'error' ? 'error' : 'textSecondary'} gutterBottom>
                {message}
              </Typography>
            )}

            {(status === 'loading' || status === 'extracting') && (
              <Box display="flex" alignItems="center" style={{ gap: 8 }} my={2}>
                <CircularProgress size={20} />
                <Typography>
                  {status === 'loading' ? 'Loading…' : 'Extracting tasks from the transcript…'}
                </Typography>
              </Box>
            )}

            {rows.length > 0 && (
              <>
                <StatusPill backgroundColor="#2BB073" style={{ marginTop: 8 }}>
                  {`${rows.length} Draft Task${rows.length === 1 ? '' : 's'} Extracted`}
                </StatusPill>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 8,
                    marginTop: 8,
                  }}
                >
                  {/* Same red as the Discard buttons below, since this count is what they act on. */}
                  <StatusPill backgroundColor="#ED3F26">
                    {`${unmatchedRows.length} Unmatched`}
                  </StatusPill>
                  <StatusPill backgroundColor="#0947B9">
                    {`${matchedRows.length} Matched to Project Team Member${
                      matchedRows.length === 1 ? '' : 's'
                    }`}
                  </StatusPill>
                </div>
                {isMobile ? (
                  <>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        marginTop: 24,
                      }}
                    >
                      <Typography style={{ fontSize: '18px', fontWeight: 500, marginBottom: 8 }}>
                        Default Due Date:
                      </Typography>
                      <KeyboardDatePicker
                        fullWidth
                        inputVariant="outlined"
                        disableToolbar
                        variant="inline"
                        format="MM/DD/YYYY"
                        value={bulkDueDate ? dayjs(bulkDueDate) : null}
                        onChange={(date) => applyBulkDueDate(date ? date.toISOString() : null)}
                        autoOk
                      />
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        marginTop: 16,
                      }}
                    >
                      <Typography style={{ fontSize: '18px', fontWeight: 500, marginBottom: 8 }}>
                        Default Priority:
                      </Typography>
                      <TextField
                        fullWidth
                        select
                        variant="outlined"
                        value={bulkPriority}
                        onChange={(e) => applyBulkPriority(e.target.value as DocumentPriorityType)}
                      >
                        <MenuItem value={DocumentPriorityType.High}>
                          {PRIORITY_LABELS[DocumentPriorityType.High]}
                        </MenuItem>
                        <MenuItem value={DocumentPriorityType.Medium}>
                          {PRIORITY_LABELS[DocumentPriorityType.Medium]}
                        </MenuItem>
                        <MenuItem value={DocumentPriorityType.Low}>
                          {PRIORITY_LABELS[DocumentPriorityType.Low]}
                        </MenuItem>
                      </TextField>
                    </div>
                    {sourceFile && (
                      <div style={{ marginTop: 16 }}>
                        <Typography style={{ fontSize: '18px', fontWeight: 500, marginBottom: 8 }}>
                          Default File Attachment:
                        </Typography>
                        <Box className={classes.fileBox} style={{ width: '100%' }}>
                          <PDFIcon />
                          <Typography className={classes.fileName}>{sourceFile.name}</Typography>
                        </Box>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Desktop: labels in a left column, the due-date/priority fields in a
                      column to the right of the labels; the file attachment box (if any)
                      spans full width below both columns. */}
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'auto auto',
                        columnGap: 16,
                        rowGap: 16,
                        alignItems: 'center',
                        marginTop: 24,
                        width: '100%',
                      }}
                    >
                      <Typography style={{ fontSize: '18px', fontWeight: 500 }}>
                        Default Due Date:
                      </Typography>
                      <KeyboardDatePicker
                        inputVariant="outlined"
                        disableToolbar
                        variant="inline"
                        format="MM/DD/YYYY"
                        value={bulkDueDate ? dayjs(bulkDueDate) : null}
                        onChange={(date) => applyBulkDueDate(date ? date.toISOString() : null)}
                        autoOk
                      />
                      <Typography style={{ fontSize: '18px', fontWeight: 500 }}>
                        Default Priority:
                      </Typography>
                      <TextField
                        select
                        variant="outlined"
                        value={bulkPriority}
                        onChange={(e) => applyBulkPriority(e.target.value as DocumentPriorityType)}
                      >
                        <MenuItem value={DocumentPriorityType.High}>
                          {PRIORITY_LABELS[DocumentPriorityType.High]}
                        </MenuItem>
                        <MenuItem value={DocumentPriorityType.Medium}>
                          {PRIORITY_LABELS[DocumentPriorityType.Medium]}
                        </MenuItem>
                        <MenuItem value={DocumentPriorityType.Low}>
                          {PRIORITY_LABELS[DocumentPriorityType.Low]}
                        </MenuItem>
                      </TextField>
                      {sourceFile && (
                        <Typography style={{ fontSize: '18px', fontWeight: 500 }}>
                          Default Attachment:
                        </Typography>
                      )}
                      {sourceFile && (
                        <Box className={classes.fileBox} style={{ width: '100%' }}>
                          <PDFIcon />
                          <Typography title={sourceFile.name} className={classes.fileName}>
                            {sourceFile.name}
                          </Typography>
                        </Box>
                      )}
                    </div>
                  </>
                )}
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => setAssociationDialogOpen(true)}
                  fullWidth
                  style={{
                    marginTop:
                      sourceDocumentUserIds.length > 0 || sourceDocumentUserGroupIds.length > 0
                        ? 16
                        : 24,
                  }}
                >
                  Define Notification List
                </Button>
                <Box display="flex" flexDirection="column" mt={3} style={{ gap: 24 }}>
                  {companyGroups.map((group) => {
                    const isGroupOpen = expandedGroupKeys.has(group.key);
                    const groupSelectedRows = group.rows.filter((row) => row.selected);
                    const groupSelectAllChecked =
                      group.rows.length > 0 && groupSelectedRows.length === group.rows.length;
                    const groupSelectAllIndeterminate =
                      groupSelectedRows.length > 0 && groupSelectedRows.length < group.rows.length;
                    return (
                      <div key={group.key}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          style={{
                            gap: 8,
                            padding: '8px 12px',
                            backgroundColor: '#EFEFEF',
                            borderRadius: 4,
                          }}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            // minWidth: 0 lets this flex item shrink below its content's natural
                            // width, which the company name's ellipsis truncation below needs to
                            // actually kick in instead of forcing the row to overflow/wrap.
                            style={{ gap: 4, cursor: 'pointer', minWidth: 0, flex: 1 }}
                            onClick={() => toggleGroupCollapsed(group.key)}
                          >
                            <Checkbox
                              color="primary"
                              checked={groupSelectAllChecked}
                              indeterminate={groupSelectAllIndeterminate}
                              classes={{
                                root: classes.groupCheckbox,
                                indeterminate: classes.groupCheckboxIndeterminate,
                              }}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => setGroupSelected(group, e.target.checked)}
                              style={{ flexShrink: 0, paddingLeft: 0, paddingRight: 4 }}
                            />
                            <Box style={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                title={group.companyName}
                                style={{
                                  fontWeight: 500,
                                  fontSize: 18,
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {group.companyName}
                              </Typography>
                              {group.userNames.length > 0 && (
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                  title={group.userNames.join(', ')}
                                  style={{
                                    fontSize: 13,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {group.userNames.join(', ')}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <StatusPill
                            onClick={() => toggleGroupCollapsed(group.key)}
                            backgroundColor="#0947B9"
                            style={{ flexShrink: 0, cursor: 'pointer' }}
                          >
                            {`${group.rows.length} Task${group.rows.length === 1 ? '' : 's'}`}
                          </StatusPill>
                          <IconButton
                            size="small"
                            onClick={() => toggleGroupCollapsed(group.key)}
                            style={{ padding: 4, paddingLeft: 0, flexShrink: 0 }}
                          >
                            {isGroupOpen ? (
                              <ExpandLess fontSize="large" htmlColor="#0947B9" />
                            ) : (
                              <ExpandMore fontSize="large" htmlColor="#0947B9" />
                            )}
                          </IconButton>
                        </Box>
                        <Collapse in={isGroupOpen}>
                          <Box
                            display="flex"
                            flexDirection="column"
                            style={{ gap: 16, marginTop: 16 }}
                          >
                            {group.rows.map((row) => (
                              <Paper
                                key={row.rowKey}
                                elevation={6}
                                className={classes.card}
                                // Same red as the Discard buttons until the task has a matched
                                // assignee, then blue like every other card.
                                style={{
                                  borderLeft: `12px solid ${
                                    isRowMatched(row) ? '#0947B9' : '#ED3F26'
                                  }`,
                                }}
                              >
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  style={{ gap: 12, minWidth: 0 }}
                                >
                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                  >
                                    <Checkbox
                                      color="primary"
                                      checked={row.selected}
                                      onChange={(e) =>
                                        updateRow(row.rowKey, { selected: e.target.checked })
                                      }
                                      style={{ padding: 0 }}
                                    />
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          color="primary"
                                          checked={row.forInformationOnly}
                                          onChange={(e) =>
                                            // Toggling For Information Only never changes the assignee.
                                            updateRow(row.rowKey, {
                                              forInformationOnly: e.target.checked,
                                            })
                                          }
                                        />
                                      }
                                      label="For Information Only"
                                      style={{ marginRight: 0 }}
                                    />
                                  </Box>
                                  {/* Show "unmatched" whenever a task needs an assignee but its
                          assignee is not one of the eligible users — no assignee id, or an
                          id with no matching user. */}
                                  {!isRowMatched(row) && (
                                    <Tooltip
                                      placement="right"
                                      title="No matched user — assign manually."
                                    >
                                      <Typography
                                        variant="caption"
                                        color="error"
                                        style={{ width: 'fit-content' }}
                                      >
                                        unmatched
                                      </Typography>
                                    </Tooltip>
                                  )}
                                  <TextField
                                    select
                                    variant="outlined"
                                    label="Assignee"
                                    fullWidth
                                    error={!row.assigneeUserId}
                                    InputLabelProps={{ className: classes.fieldLabel }}
                                    value={row.assigneeUserId ?? ''}
                                    onChange={(e) =>
                                      updateRow(row.rowKey, {
                                        assigneeUserId: (e.target.value as string) || null,
                                      })
                                    }
                                  >
                                    <MenuItem disabled value="">
                                      <em>Transcript: {row.rawAssigneeName}</em>
                                    </MenuItem>
                                    {users.map((u) => (
                                      <MenuItem key={u.id} value={u.id}>
                                        <AssignedUserText
                                          name={u.name + ` (${u.securityGroupName})`}
                                          company={u.companyName}
                                        />
                                      </MenuItem>
                                    ))}
                                  </TextField>

                                  <TextField
                                    label="Title"
                                    variant="outlined"
                                    fullWidth
                                    InputLabelProps={{ className: classes.fieldLabel }}
                                    value={row.title}
                                    onChange={(e) =>
                                      updateRow(row.rowKey, { title: e.target.value })
                                    }
                                  />
                                  <TextField
                                    label="Description"
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    rows={5}
                                    InputLabelProps={{ className: classes.fieldLabel }}
                                    value={row.description}
                                    onChange={(e) =>
                                      updateRow(row.rowKey, { description: e.target.value })
                                    }
                                  />
                                  <KeyboardDatePicker
                                    label="Due"
                                    inputVariant="outlined"
                                    disableToolbar
                                    variant="inline"
                                    format="MM/DD/YYYY"
                                    fullWidth
                                    disabled={row.forInformationOnly}
                                    InputLabelProps={{ className: classes.fieldLabel }}
                                    value={row.dueDate ? dayjs(row.dueDate) : null}
                                    onChange={(date) =>
                                      updateRow(row.rowKey, {
                                        dueDate: date ? date.toISOString() : null,
                                      })
                                    }
                                    autoOk
                                  />

                                  <TextField
                                    select
                                    label="Priority"
                                    variant="outlined"
                                    fullWidth
                                    disabled={row.forInformationOnly}
                                    InputLabelProps={{ className: classes.fieldLabel }}
                                    value={row.priority}
                                    onChange={(e) =>
                                      updateRow(row.rowKey, {
                                        priority: e.target.value as DocumentPriorityType,
                                      })
                                    }
                                  >
                                    <MenuItem value={DocumentPriorityType.High}>
                                      {PRIORITY_LABELS[DocumentPriorityType.High]}
                                    </MenuItem>
                                    <MenuItem value={DocumentPriorityType.Medium}>
                                      {PRIORITY_LABELS[DocumentPriorityType.Medium]}
                                    </MenuItem>
                                    <MenuItem value={DocumentPriorityType.Low}>
                                      {PRIORITY_LABELS[DocumentPriorityType.Low]}
                                    </MenuItem>
                                  </TextField>

                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<Publish />}
                                    onClick={() => setFileUploadRowKey(row.rowKey)}
                                  >
                                    Upload File
                                  </Button>
                                  {/* Uploaded files (attached to the task on publish), below the button. */}
                                  {row.uploadedFiles.length > 0 && (
                                    <div>
                                      <div style={{ fontSize: '15px' }}>FILES:</div>
                                      {row.uploadedFiles.map((file) => (
                                        <Box
                                          key={file.stagingKey}
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="space-between"
                                        >
                                          <Typography
                                            variant="body2"
                                            style={{
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                            }}
                                          >
                                            {file.name}
                                          </Typography>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              removeUploadedFile(row.rowKey, file.stagingKey)
                                            }
                                          >
                                            <Clear fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      ))}
                                    </div>
                                  )}

                                  {/* Tagged existing documents (associated with the task on publish). */}
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => openAssociateDialog(row)}
                                    style={{ lineHeight: 1, height: 'unset', minHeight: 32 }}
                                  >
                                    Tag Existing Documents
                                  </Button>
                                  {/* Tagged docs grouped by document type, like AddDocumentDialog. */}
                                  {Array.from(
                                    new Set(row.associatedDocs.map((doc) => doc.type)),
                                  ).map((type) => (
                                    <div key={type} style={{ marginBottom: 8 }}>
                                      <Typography style={{ fontWeight: 500 }}>
                                        {getUserFriendlyDocumentTemplateNamePlural(
                                          type as DocumentTemplateType,
                                        )}
                                      </Typography>
                                      {row.associatedDocs
                                        .filter((doc) => doc.type === type)
                                        .map((doc) => (
                                          <Box
                                            key={doc.id}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                          >
                                            <Typography
                                              variant="body2"
                                              style={{
                                                lineHeight: '18px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                              }}
                                            >
                                              {doc.title}
                                            </Typography>
                                            <IconButton
                                              size="small"
                                              onClick={() => removeTaggedDoc(row.rowKey, doc.id)}
                                            >
                                              <Clear fontSize="small" />
                                            </IconButton>
                                          </Box>
                                        ))}
                                    </div>
                                  ))}

                                  <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent={
                                      publishingRowKey === row.rowKey ? 'center' : 'flex-end'
                                    }
                                    style={{ gap: 8, paddingTop: 16 }}
                                  >
                                    {publishingRowKey === row.rowKey ? (
                                      <>
                                        <CircularProgress size={20} />
                                        <Typography>Publishing…</Typography>
                                      </>
                                    ) : (
                                      <>
                                        <Button
                                          variant="contained"
                                          onClick={() => setDiscardCandidates([row])}
                                          disabled={status === 'publishing' || !!publishingRowKey}
                                          className={classes.deleteButton}
                                        >
                                          Discard
                                        </Button>
                                        <Button
                                          variant="contained"
                                          color="primary"
                                          onClick={() => void publishRows([row], row.rowKey)}
                                          disabled={
                                            status === 'publishing' ||
                                            !!publishingRowKey ||
                                            !isRowPublishable(row)
                                          }
                                        >
                                          Publish
                                        </Button>
                                      </>
                                    )}
                                  </Box>
                                </Box>
                              </Paper>
                            ))}
                          </Box>
                        </Collapse>
                      </div>
                    );
                  })}
                </Box>
              </>
            )}
            {status === 'ready' && rows.length === 0 && !message && (
              <Typography color="textSecondary">
                {hasResolvedTasks
                  ? 'Every extracted task has been published or discarded. Nothing is left to review.'
                  : 'No action items were found in this transcript.'}
              </Typography>
            )}
          </DialogContent>
        </MuiPickersUtilsProvider>
        {rows.length > 0 && (
          <DialogActions style={{ padding: 16 }}>
            {status === 'publishing' ? (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="100%"
                style={{ gap: 8 }}
                px={1}
              >
                <CircularProgress size={28} />
                <Typography>Publishing…</Typography>
              </Box>
            ) : (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
                <Button
                  variant="contained"
                  onClick={() => setDiscardCandidates(selectedRows)}
                  disabled={selectedRows.length === 0 || !!publishingRowKey}
                  className={classes.deleteButton}
                >
                  Discard Selected
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => void publishRows(selectedRows)}
                  disabled={!canPublishSelected || !!publishingRowKey}
                  style={{ lineHeight: 1, height: 'unset' }}
                >
                  Publish Selected
                </Button>
              </div>
            )}
          </DialogActions>
        )}
      </Dialog>

      <Dialog
        open={!!discardCandidates}
        onClose={() => (isDiscarding ? undefined : setDiscardCandidates(null))}
        maxWidth="xs"
      >
        <DialogTitle disableTypography className={classes.title}>
          {`Discard ${discardCandidates?.length ?? 0} Task${
            (discardCandidates?.length ?? 0) === 1 ? '' : 's'
          }?`}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {(discardCandidates?.length ?? 0) === 1 ? 'This task' : 'These tasks'} will be removed
            from the review list and cannot be brought back.
          </Typography>
        </DialogContent>
        <DialogActions style={{ display: 'flex', justifyContent: 'space-between', padding: 16 }}>
          <CancelButton onClick={() => setDiscardCandidates(null)} disabled={isDiscarding} />
          <Button
            variant="contained"
            className={classes.deleteButton}
            onClick={() => void confirmDiscard()}
            disabled={isDiscarding}
          >
            {isDiscarding ? <CircularProgress size={20} /> : 'Discard'}
          </Button>
        </DialogActions>
      </Dialog>

      <FileUploadDialog
        open={!!fileUploadRowKey}
        title="Upload File"
        newFiles={localFiles}
        addFile={(file) => setLocalFiles([file as File])}
        addFiles={(files) => setLocalFiles((prev) => [...prev, ...(files as File[])])}
        removeFile={(file) => setLocalFiles((prev) => prev.filter((f) => f !== (file as File)))}
        canSubmit={localFiles.length > 0}
        buttonType="submit"
        uploadMessage={fileUploadMessage}
        uploadProgress={uploadProgress}
        handleSubmit={uploadRowFilesToStaging}
        disableComments
        disableDesignUpload
        allowMultiple
        handleClose={() => {
          setLocalFiles([]);
          setFileUploadRowKey(null);
        }}
      />

      <AssociateDocumentDialog
        open={!!associateRowKey}
        handleClose={closeAssociateDialog}
        documents={workingDocTree}
        setDocuments={setWorkingDocTree}
      />

      <ManagePermissionsDialog
        dialogOpen={associationDialogOpen}
        closeDialog={() => {
          // repositions popover window to accommodate for InlineNotificationList
          window.dispatchEvent(new CustomEvent('resize'));
          setAssociationDialogOpen(false);
        }}
        type={ManagePermissionsDialogType.AssociatedUsers}
        associatedGroups={associatedGroups}
        associatedUsers={associatedUsers}
        setAssociatedUsers={setAssociatedUsers}
        setAssociatedGroups={setAssociatedGroups}
        targetDocumentType={targetTaskType}
      />
    </>
  );
};

export default MeetingMinutesTaskReviewPanel;
